import { normalizeCheckoutOfferSource, type CheckoutTrackingPayload } from '@/services/analytics/checkout';
import { readCookieConsentFromDocument } from '@/utils/cookieConsent.utils';
import { isTypeOfString } from 'common/utils/string.utils';

const PENDING_CHECKOUT_CONTEXT_KEY = 'analytics:checkout:pending';

function getTrackingStorage(): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    const storage = window.sessionStorage;
    const consent = readCookieConsentFromDocument();
    if (!consent?.analytics && !consent?.marketing) {
      storage.removeItem(PENDING_CHECKOUT_CONTEXT_KEY);
      return null;
    }
    return storage;
  } catch {
    return null;
  }
}

export function savePendingCheckoutContext(context: CheckoutTrackingPayload): void {
  try {
    getTrackingStorage()?.setItem(PENDING_CHECKOUT_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Analytics storage is optional, even when the browser denies writes or runs out of space.
  }
}

export function consumePendingCheckoutContext(): CheckoutTrackingPayload | null {
  try {
    const storage = getTrackingStorage();
    if (!storage) return null;
    const rawValue = storage.getItem(PENDING_CHECKOUT_CONTEXT_KEY);
    storage.removeItem(PENDING_CHECKOUT_CONTEXT_KEY);
    if (!rawValue) return null;

    const context: Partial<CheckoutTrackingPayload> | null = JSON.parse(rawValue);
    if (
      !context ||
      !isTypeOfString(context.currency) ||
      !isTypeOfString(context.source) ||
      typeof context.quantity !== 'number' ||
      !Number.isFinite(context.quantity)
    )
      return null;

    return {
      ...context,
      currency: context.currency,
      source: context.source,
      quantity: context.quantity,
      offer_source: normalizeCheckoutOfferSource(context.offer_source) ?? undefined,
    };
  } catch {
    return null;
  }
}

export function clearPendingCheckoutContext(): void {
  try {
    getTrackingStorage()?.removeItem(PENDING_CHECKOUT_CONTEXT_KEY);
  } catch {
    // A failed telemetry cleanup must not interrupt checkout or navigation.
  }
}
